import React, { useState, useEffect } from 'react';
import {
  Typography,
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Grid,
  Avatar,
  Divider,
  Alert,
  IconButton,
} from '@mui/material';
import {
  Business,
  Phone,
  Email,
  LocationOn,
  Edit,
  Save,
  Cancel,
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { SalonService } from '../services/salonService';
import { Salon } from '../types';

export default function SalonProfile() {
  const { appUser } = useAuth();
  const [salon, setSalon] = useState<Salon | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
  });

  const loadSalon = async () => {
    if (!appUser?.salonId) {
      setError('User not properly authenticated');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const salonData = await SalonService.getSalon(appUser.salonId);
      if (salonData) {
        setSalon(salonData);
        setFormData({
          name: salonData.name || '',
          phone: salonData.phone || '',
          email: salonData.email || '',
          address: salonData.address || '',
        });
      }
      setError(null);
    } catch (err) {
      console.error('Error loading salon:', err);
      setError('Failed to load salon information');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSalon();
  }, [appUser]);

  const handleEdit = () => {
    setEditing(true);
    setError(null);
    setSuccess(null);
  };

  const handleCancel = () => {
    if (salon) {
      setFormData({
        name: salon.name || '',
        phone: salon.phone || '',
        email: salon.email || '',
        address: salon.address || '',
      });
    }
    setEditing(false);
    setError(null);
    setSuccess(null);
  };

  const handleSave = async () => {
    if (!appUser?.salonId) return;

    try {
      setSaving(true);
      await SalonService.updateSalon(appUser.salonId, formData);
      setSuccess('Salon information updated successfully!');
      await loadSalon(); // Reload to get updated data
      setEditing(false);
    } catch (err) {
      console.error('Error saving salon:', err);
      setError('Failed to update salon information');
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: event.target.value
    }));
  };

  if (loading) {
    return (
      <Box>
        <Typography variant="h4" gutterBottom>
          Salon Profile
        </Typography>
        <Typography>Loading salon information...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom>
          Salon Profile
        </Typography>
        {!editing && (
          <Button
            variant="contained"
            startIcon={<Edit />}
            onClick={handleEdit}
          >
            Edit Profile
          </Button>
        )}
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Card>
        <CardContent>
          {/* Salon Header */}
          <Box display="flex" alignItems="center" mb={4}>
            <Avatar
              sx={{
                width: 80,
                height: 80,
                bgcolor: 'primary.main',
                mr: 3
              }}
            >
              <Business sx={{ fontSize: 40 }} />
            </Avatar>
            <Box>
              <Typography variant="h4" color="primary">
                {salon?.name || 'Salon Name'}
              </Typography>
              <Typography variant="body1" color="text.secondary">
                Salon ID: {salon?.id}
              </Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          {/* Salon Information */}
          <Grid container spacing={3}>
            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Business color="action" sx={{ mr: 2 }} />
                <Typography variant="h6">Basic Information</Typography>
              </Box>

              <TextField
                fullWidth
                label="Salon Name"
                value={formData.name}
                onChange={handleInputChange('name')}
                disabled={!editing}
                sx={{ mb: 2 }}
                variant={editing ? 'outlined' : 'standard'}
                InputProps={{
                  readOnly: !editing,
                }}
              />

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={formData.email}
                onChange={handleInputChange('email')}
                disabled={!editing}
                sx={{ mb: 2 }}
                variant={editing ? 'outlined' : 'standard'}
                InputProps={{
                  readOnly: !editing,
                }}
              />
            </Grid>

            <Grid size={{ xs: 12, md: 6 }}>
              <Box display="flex" alignItems="center" mb={2}>
                <Phone color="action" sx={{ mr: 2 }} />
                <Typography variant="h6">Contact Details</Typography>
              </Box>

              <TextField
                fullWidth
                label="Phone Number"
                value={formData.phone}
                onChange={handleInputChange('phone')}
                disabled={!editing}
                sx={{ mb: 2 }}
                variant={editing ? 'outlined' : 'standard'}
                InputProps={{
                  readOnly: !editing,
                }}
              />

              <Box display="flex" alignItems="flex-start" mb={2}>
                <LocationOn color="action" sx={{ mr: 2, mt: 1 }} />
                <TextField
                  fullWidth
                  label="Address"
                  multiline
                  rows={3}
                  value={formData.address}
                  onChange={handleInputChange('address')}
                  disabled={!editing}
                  variant={editing ? 'outlined' : 'standard'}
                  InputProps={{
                    readOnly: !editing,
                  }}
                />
              </Box>
            </Grid>
          </Grid>

          {/* Edit Actions */}
          {editing && (
            <Box display="flex" justifyContent="flex-end" gap={2} mt={3}>
              <Button
                variant="outlined"
                startIcon={<Cancel />}
                onClick={handleCancel}
                disabled={saving}
              >
                Cancel
              </Button>
              <Button
                variant="contained"
                startIcon={<Save />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Salon Stats Card */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Quick Stats
          </Typography>
          <Grid container spacing={2}>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Member Since
              </Typography>
              <Typography variant="body1">
                {salon?.createdAt ? salon.createdAt.toLocaleDateString('en-NG') : 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Last Updated
              </Typography>
              <Typography variant="body1">
                {salon?.updatedAt ? salon.updatedAt.toLocaleDateString('en-NG') : 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Owner ID
              </Typography>
              <Typography variant="body1">
                {salon?.ownerId ? salon.ownerId.substring(0, 8) + '...' : 'N/A'}
              </Typography>
            </Grid>
            <Grid size={{ xs: 6, sm: 3 }}>
              <Typography variant="body2" color="text.secondary">
                Status
              </Typography>
              <Typography variant="body1" color="success.main">
                Active
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>
    </Box>
  );
}
